import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileText,
  Loader2,
} from 'lucide-react';
import { BulkUploadValidationSummaryView } from '../../../../api/General/bulkUpload';

interface ValidationSummaryStepProps {
  validation: BulkUploadValidationSummaryView | null;
  loading: boolean;
  error: string | null;
}

export default function ValidationSummaryStep({
  validation,
  loading,
  error,
}: ValidationSummaryStepProps) {
  const { t } = useTranslation('common');

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm text-muted-foreground">{t('Validating documents...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-16 text-center">
        <XCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (!validation) return null;

  return (
    <div className="px-6 py-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground">{t('Validation Summary')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('Review the validation results before processing')}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="flex items-center gap-3 p-4 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-800">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30">
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-green-700 dark:text-green-400">
              {validation.validCount}
            </p>
            <p className="text-xs text-green-600 dark:text-green-500">{t('Valid')}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">
              {validation.warningCount}
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-500">{t('Warnings')}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-red-700 dark:text-red-400">
              {validation.errorCount}
            </p>
            <p className="text-xs text-red-600 dark:text-red-500">{t('Errors')}</p>
          </div>
        </div>
      </div>

      {/* Ready to process banner */}
      {validation.errorCount === 0 && validation.validCount > 0 && (
        <div className="flex items-center gap-3 p-3 mb-6 rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/10 dark:border-green-800">
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
          <p className="text-sm text-green-700 dark:text-green-400">
            {t('All documents are valid and ready to be processed. Click "Next" to begin processing.')}
          </p>
        </div>
      )}

      {validation.errorCount > 0 && (
        <div className="flex items-center gap-3 p-3 mb-6 rounded-lg bg-red-50 border border-red-200 dark:bg-red-900/10 dark:border-red-800">
          <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">
            {t('Some documents have errors. Go back and fix the mappings before processing.')}
          </p>
        </div>
      )}

      {/* Document list */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('File')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-28">
                  {t('Status')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('Details')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {validation.documents.map((doc) => (
                <tr key={doc.documentId} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="truncate max-w-[300px]">{doc.originalFileName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {doc.validationStatus === 'Valid' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        <CheckCircle2 className="w-3 h-3" />
                        {t('Valid')}
                      </span>
                    )}
                    {doc.validationStatus === 'Warning' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <AlertTriangle className="w-3 h-3" />
                        {t('Warning')}
                      </span>
                    )}
                    {doc.validationStatus === 'Error' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        <XCircle className="w-3 h-3" />
                        {t('Error')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {doc.messages.length > 0 ? (
                      <ul className="space-y-0.5">
                        {doc.messages.map((msg, idx) => (
                          <li key={idx} className="text-xs text-muted-foreground">
                            {msg}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-xs text-muted-foreground">{t('Ready to process')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
